import { useState } from "react";
import { FileText, Download, Upload, Loader2, Send, FilePlus2, Paperclip, Trash2 } from "lucide-react";
import { apiFetch, openPdf, downloadFile, uploadFile } from "@/portal/api";
import { PROTOCOL_CATS, CAT_LABEL } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";

const ContractRow = ({ contract, token, onChange }) => {
  const [uploading, setUploading] = useState(null);
  const [showProto, setShowProto] = useState(false);
  const [generating, setGenerating] = useState(false);
  const emptyProto = { category: "handover", odometer_km: "", fuel: "", description: "", damage_new: "", damage_existing: "", place: "Praha" };
  const [proto, setProto] = useState(emptyProto);
  const [sending, setSending] = useState(false);

  const sendToSign = async () => {
    const isSigned = contract.status === "signed";
    if (isSigned) {
      const ok = await confirmDialog({
        title: "Smlouva je už podepsaná",
        description: "Opětovným odesláním se předchozí podpis ZRUŠÍ a klient bude vyzván k novému podpisu s aktuálními údaji. Pokračovat?",
        confirmText: "Odeslat znovu", danger: true,
      });
      if (!ok) return;
    }
    setSending(true);
    try {
      const r = await apiFetch(`/admin/contracts/${contract.id}/send-to-sign`, { method: "POST", token });
      if (r.email_sent) toast.success("Smlouva odeslána klientovi k podpisu.");
      else toast.error("Smlouva připravena k podpisu, ale e-mail se nepodařilo odeslat (zkontrolujte ověření domény v Resend).");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const generateProtocol = async () => {
    setGenerating(true);
    try {
      await apiFetch(`/admin/contracts/${contract.id}/protocols/generate`, { method: "POST", token, body: proto });
      setProto(emptyProto);
      setShowProto(false);
      toast.success("Protokol vygenerován.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const onPick = async (e, category) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(category);
    try {
      for (const file of files) {
        await uploadFile(`/admin/contracts/${contract.id}/protocols`, file, token, { category });
      }
      toast.success("Soubor(y) nahrány.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(null);
    }
  };

  const removeProtocol = async (id) => {
    const ok = await confirmDialog({ title: "Smazat protokol?", confirmText: "Smazat", danger: true });
    if (!ok) return;
    try {
      await apiFetch(`/admin/protocols/${id}`, { method: "DELETE", token });
      toast.success("Protokol smazán.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const grouped = (contract.protocols || []).reduce((acc, p) => {
    const c = p.category || "protocol";
    (acc[c] = acc[c] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="rounded-xl bg-white/5 p-3" data-testid={`admin-contract-${contract.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => openPdf(`/admin/contracts/${contract.id}/pdf`, token).catch((e) => toast.error(e.message))}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/20 hover:bg-indigo-500/25 transition-colors"
          data-testid={`contract-pdf-${contract.id}`}
        >
          <FileText className="w-3.5 h-3.5" /> Smlouva ({contract.car_label})
          {contract.status === "signed"
            ? <span className="text-green-400">· podepsáno</span>
            : <span className="text-amber-400">· čeká na podpis</span>}
        </button>

        <button
          onClick={() => downloadFile(`/admin/contracts/${contract.id}/pdf`, token, `smlouva-${contract.id.slice(0, 8)}.pdf`).catch((e) => toast.error(e.message))}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10 transition-colors"
          title="Stáhnout smlouvu (PDF)"
          data-testid={`contract-download-${contract.id}`}
        >
          <Download className="w-3.5 h-3.5" /> Stáhnout
        </button>

        {PROTOCOL_CATS.map((cat) => (
          <label key={cat.key} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5 transition-colors cursor-pointer" data-testid={`protocol-upload-label-${cat.testid}-${contract.id}`}>
            {uploading === cat.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {cat.label}
            <input type="file" accept=".pdf,image/*" multiple className="hidden" onChange={(e) => onPick(e, cat.key)} disabled={uploading !== null} data-testid={`protocol-input-${cat.testid}-${contract.id}`} />
          </label>
        ))}

        <button
          onClick={() => setShowProto((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/25 transition-colors"
          data-testid={`protocol-create-toggle-${contract.id}`}
        >
          <FilePlus2 className="w-3.5 h-3.5" /> Vytvořit protokol
        </button>

        <button
          onClick={sendToSign}
          disabled={sending}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/20 hover:bg-sky-500/25 transition-colors disabled:opacity-60"
          data-testid={`contract-send-sign-${contract.id}`}
        >
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {contract.status === "signed" ? "Znovu poslat k podpisu" : "Poslat k podpisu"}
        </button>
      </div>

      {showProto && (
        <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-2" data-testid={`protocol-form-${contract.id}`}>
          <div className="flex gap-2 p-1 bg-white/5 rounded-full w-full max-w-xs">
            <button type="button" onClick={() => setProto({ ...proto, category: "handover" })} className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${proto.category === "handover" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid={`protocol-cat-handover-${contract.id}`}>Převzetí</button>
            <button type="button" onClick={() => setProto({ ...proto, category: "return" })} className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${proto.category === "return" ? "bg-emerald-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid={`protocol-cat-return-${contract.id}`}>Vrácení</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input className="input-dark" placeholder="Stav tachometru (km)" value={proto.odometer_km} onChange={(e) => setProto({ ...proto, odometer_km: e.target.value })} data-testid={`protocol-km-${contract.id}`} />
            <input className="input-dark" placeholder="Stav paliva (např. plná)" value={proto.fuel} onChange={(e) => setProto({ ...proto, fuel: e.target.value })} data-testid={`protocol-fuel-${contract.id}`} />
            <input className="input-dark" placeholder="Místo" value={proto.place} onChange={(e) => setProto({ ...proto, place: e.target.value })} data-testid={`protocol-place-${contract.id}`} />
          </div>
          <textarea className="input-dark w-full" rows={2} placeholder="Popis stavu vozidla" value={proto.description} onChange={(e) => setProto({ ...proto, description: e.target.value })} data-testid={`protocol-desc-${contract.id}`} />
          <textarea className="input-dark w-full" rows={2} placeholder="Nově vzniklá poškození" value={proto.damage_new} onChange={(e) => setProto({ ...proto, damage_new: e.target.value })} data-testid={`protocol-damage-new-${contract.id}`} />
          <textarea className="input-dark w-full" rows={2} placeholder="Stávající poškození (převzato s vozidlem)" value={proto.damage_existing} onChange={(e) => setProto({ ...proto, damage_existing: e.target.value })} data-testid={`protocol-damage-existing-${contract.id}`} />
          <p className="text-[11px] text-zinc-500">Fotky nahrajte tlačítky „Foto převzetí / Foto vrácení". Protokol bude podepsán za pronajímatele automaticky.</p>
          <div className="flex gap-2">
            <button onClick={generateProtocol} disabled={generating} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60" data-testid={`protocol-generate-${contract.id}`}>
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5" />} Vygenerovat protokol (PDF)
            </button>
            <button onClick={() => setShowProto(false)} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5">Zrušit</button>
          </div>
        </div>
      )}

      {Object.keys(grouped).length > 0 && (
        <div className="mt-2 space-y-2">
          {["protocol", "handover", "return"].filter((c) => grouped[c]).map((c) => (
            <div key={c}>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{CAT_LABEL[c]}</p>
              <div className="space-y-1">
                {grouped[c].map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                    <button
                      onClick={() => openPdf(`/admin/protocols/${p.id}/file`, token).catch((e) => toast.error(e.message))}
                      className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white min-w-0 text-left break-all"
                      data-testid={`protocol-view-${p.id}`}
                    >
                      <Paperclip className="w-3.5 h-3.5 text-indigo-400 shrink-0" /> {p.original_filename}
                    </button>
                    <button
                      onClick={() => downloadFile(`/admin/protocols/${p.id}/file`, token, p.original_filename || `protokol-${p.id.slice(0, 8)}.pdf`).catch((e) => toast.error(e.message))}
                      className="text-zinc-500 hover:text-indigo-300 p-1"
                      title="Stáhnout protokol"
                      data-testid={`protocol-download-${p.id}`}
                    >
                      <Download className="w-3 h-3" />
                    </button>
                    <button onClick={() => removeProtocol(p.id)} className="text-zinc-500 hover:text-red-400 p-1" title="Smazat" data-testid={`protocol-delete-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractRow;
